import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { FeesApi, OrgFeesApi } from '@/services/fees.api';
import { NooraniGroupsApi } from '@/services/noorani-groups.api';
import { Modal } from '@/components/ui/Modal';
import { Field, Input } from '@/components/ui/Input';
import { toast } from '@/stores/toast.store';

const formSchema = z.object({
  prefixName: z.string().min(1, 'Required').max(191),
  dueDate: z.string().min(1, 'Required'),
});
type FormValues = z.infer<typeof formSchema>;

interface FeeRow {
  feeType: string;
  amount: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Set only when opened from the org-tier Noorani Fees screen — threads the picked School into every lookup/create call below instead of relying on the (nonexistent, at org tier) session's own school. */
  schoolId?: string;
}

export function CreateNooraniFeeModal({ open, onClose, schoolId }: Props) {
  const qc = useQueryClient();
  const isOrg = schoolId !== undefined;
  const [groupIds, setGroupIds] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<FeeRow[]>([{ feeType: '', amount: '' }]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  const { data: groupsPage } = useQuery({
    queryKey: ['noorani-groups', 'all-for-fee-form', schoolId],
    queryFn: () => NooraniGroupsApi.list({ schoolId, limit: 100 }),
    enabled: open && (!isOrg || !!schoolId),
  });
  const groups = groupsPage?.items ?? [];

  const { data: feeTypeSuggestions = [] } = useQuery({
    queryKey: ['fees', 'noorani', 'fee-types', schoolId],
    queryFn: () => (isOrg ? OrgFeesApi.nooraniFeeTypeSuggestions(schoolId!) : FeesApi.nooraniFeeTypeSuggestions()),
    enabled: open && (!isOrg || !!schoolId),
  });

  const resetAll = () => {
    reset({ prefixName: '', dueDate: '' });
    setGroupIds(new Set());
    setRows([{ feeType: '', amount: '' }]);
  };

  const create = useMutation({
    mutationFn: (payload: Parameters<typeof FeesApi.nooraniCreate>[0]) =>
      isOrg ? OrgFeesApi.nooraniCreate(schoolId!, payload) : FeesApi.nooraniCreate(payload),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['fees', 'noorani'] });
      qc.invalidateQueries({ queryKey: ['fees', 'report'] });
      toast.success(
        `Created ${res.itemCount} fee${res.itemCount === 1 ? '' : 's'} for ${res.studentCount} student${res.studentCount === 1 ? '' : 's'} (${res.totalDemandsCreated} demands).`,
      );
      resetAll();
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const toggleGroup = (id: string) =>
    setGroupIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAllGroups = () =>
    setGroupIds((prev) => (prev.size === groups.length ? new Set() : new Set(groups.map((g) => g.id))));

  const updateRow = (index: number, patch: Partial<FeeRow>) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, { feeType: '', amount: '' }]);
  const removeRow = (index: number) => setRows((prev) => prev.filter((_, i) => i !== index));

  const onSubmit = (values: FormValues) => {
    if (groupIds.size === 0) {
      toast.error('Select at least one Noorani group');
      return;
    }
    const items = rows
      .filter((r) => r.feeType.trim() && Number(r.amount) > 0)
      .map((r) => ({ feeType: r.feeType.trim(), amount: Number(r.amount) }));
    if (items.length === 0) {
      toast.error('Add at least one Fee Type with an amount');
      return;
    }
    create.mutate({
      prefixName: values.prefixName,
      groupIds: [...groupIds],
      items,
      dueDate: values.dueDate,
      installmentEnabled: false,
    });
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        resetAll();
        onClose();
      }}
      title="Create Noorani Fees"
      description="Create a fee for one or more Noorani groups — every student in the selected group(s) gets a demand for each fee type below."
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="create-noorani-fee-form" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Submit'}
          </button>
        </>
      }
    >
      <form id="create-noorani-fee-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Field label="Prefix Name" required error={errors.prefixName?.message}>
          <Input placeholder="e.g. Term 1 Fees" {...register('prefixName')} />
        </Field>

        <Field label="Noorani Groups" required>
          <div className="rounded-md border border-slate-200 p-3">
            <label className="flex cursor-pointer items-center gap-2 border-b border-slate-100 pb-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={groups.length > 0 && groupIds.size === groups.length}
                onChange={toggleAllGroups}
              />
              Select All
            </label>
            <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
              {groups.map((g) => (
                <label key={g.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                  <input type="checkbox" checked={groupIds.has(g.id)} onChange={() => toggleGroup(g.id)} />
                  <span className="text-slate-700">{g.name}</span>
                  <span className="text-xs text-slate-400">({g.studentCount} students)</span>
                </label>
              ))}
              {groups.length === 0 && <p className="px-2 py-1 text-sm text-slate-400">No Noorani groups found.</p>}
            </div>
            <p className="mt-2 text-xs text-slate-500">{groupIds.size} selected</p>
          </div>
        </Field>

        <Field label="Fee Type & Amount" required>
          <datalist id="noorani-fee-type-suggestions">
            {feeTypeSuggestions.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
          <div className="space-y-2">
            {rows.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  list="noorani-fee-type-suggestions"
                  placeholder="Fee type, e.g. Tuition Fee"
                  value={row.feeType}
                  onChange={(e) => updateRow(i, { feeType: e.target.value })}
                  className="w-full flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
                <input
                  type="number"
                  min={0.01}
                  step="0.01"
                  placeholder="Amount"
                  value={row.amount}
                  onChange={(e) => updateRow(i, { amount: e.target.value })}
                  className="w-32 rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  disabled={rows.length === 1}
                  className="rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                  title="Remove row"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addRow}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-brand-600 hover:bg-brand-50"
          >
            <Plus className="h-4 w-4" /> Add New Data
          </button>
        </Field>

        <Field label="Due Date" required error={errors.dueDate?.message}>
          <Input type="date" {...register('dueDate')} />
        </Field>

        <Field label="Include Fees Installment">
          <div className="flex items-center gap-4 text-sm">
            <label className="flex cursor-not-allowed items-center gap-1.5 text-slate-400" title="Coming soon">
              <input type="radio" name="installment" disabled />
              Enable
            </label>
            <label className="flex cursor-pointer items-center gap-1.5 text-slate-700">
              <input type="radio" name="installment" checked readOnly />
              Disable
            </label>
          </div>
        </Field>
      </form>
    </Modal>
  );
}

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
